/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, X } from "lucide-react";
import { markDhdWarningSeen, markDhdWarningSkipped } from "../utils/dhdHelpers";

export default function DhdWarningModal({ warning, userId, onDismissed }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const [busy, setBusy] = useState(false);
  const [seenMarked, setSeenMarked] = useState(false);

  const canSkip = !!warning?.canSkip;

  useEffect(() => {
    if (!warning?.id || !userId || seenMarked) return;
    if (warning.seenAt) {
      setSeenMarked(true);
      return;
    }
    setSeenMarked(true);
    markDhdWarningSeen(userId, warning.id).catch((err) =>
      console.error("Failed to mark warning seen:", err),
    );
  }, [warning?.id, warning?.seenAt, userId, seenMarked]);

  useEffect(() => {
    setBusy(false);
    setSeenMarked(!!warning?.seenAt);
  }, [warning?.id]);

  useEffect(() => {
    if (!warning) {
      document.body.style.overflow = "";
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [warning]);

  if (!warning) return null;

  const handleSkip = async () => {
    if (!canSkip || busy || !userId) return;
    setBusy(true);
    try {
      await markDhdWarningSkipped(userId, warning.id);
      onDismissed?.(warning.id);
    } catch (err) {
      console.error("Failed to skip warning:", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-5"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <style>{`
        @keyframes dhdWarnFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes dhdWarnSlide {
          from { opacity: 0; transform: translateY(18px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div
        className="absolute inset-0"
        style={{
          background: "rgba(15, 5, 25, 0.62)",
          backdropFilter: "blur(5px)",
          animation: "dhdWarnFade 0.22s ease",
        }}
        onClick={canSkip ? handleSkip : undefined}
      />

      <div
        className="relative w-full sm:max-w-[440px] bg-white sm:rounded-[24px] rounded-t-[24px] overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
        style={{ animation: "dhdWarnSlide 0.3s cubic-bezier(0.22,1,0.36,1)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="relative shrink-0 px-5 pt-5 pb-5 overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #1a0a00 0%, #3d1a00 40%, #5c2800 55%, #2a1200 100%)",
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none opacity-40"
            style={{
              background:
                "radial-gradient(ellipse 50% 60% at 80% 20%, rgba(255,106,0,0.35), transparent 60%)",
            }}
          />

          <div className="relative z-10 flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={{
                background: "rgba(255,106,0,0.2)",
                border: "1px solid rgba(255,106,0,0.35)",
                color: "#FF6A00",
              }}
            >
              <AlertTriangle size={22} strokeWidth={2.3} />
            </div>

            <div className="min-w-0 flex-1 pe-10">
              <h2 className="text-white text-lg sm:text-xl font-black leading-tight">
                {warning.title}
              </h2>
            </div>
          </div>

          <div className="absolute top-4 end-4 z-10 flex items-center gap-1.5">
            {canSkip && (
              <button
                type="button"
                onClick={handleSkip}
                disabled={busy}
                className="w-9 h-9 rounded-full flex items-center justify-center text-white/80 hover:text-white transition disabled:opacity-50"
                style={{ background: "rgba(0,0,0,0.25)" }}
                aria-label={t("dhdWarning.close")}
              >
                <X size={17} />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          <p className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-wrap">
            {warning.details}
          </p>
        </div>

        <div className="shrink-0 px-5 pb-5 sm:px-6 sm:pb-6 flex flex-col gap-2.5">
          {canSkip && (
            <button
              type="button"
              onClick={handleSkip}
              disabled={busy}
              className="w-full h-12 rounded-2xl font-bold text-sm text-white transition active:scale-[0.99] disabled:opacity-60"
              style={{
                background: "linear-gradient(135deg, #9b59bf 0%, #814a9f 100%)",
                boxShadow: "0 8px 20px rgba(155,89,191,0.28)",
              }}
            >
              {busy ? t("dhdWarning.saving") : t("dhdWarning.gotIt")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
